import { memo } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism"

interface MessageContentProps {
  content: string
  role: "user" | "assistant"
}

// Both roles render through the same markdown pipeline so officer-pasted
// confirmation text (numbered clauses, lists, bold) gets the same typography
// and structure as assistant replies, instead of collapsing into one
// unstyled wall of text.
// memo is load-bearing, not a nicety: during a rewrite stream every SSE
// event re-renders the whole chat, and an un-memoized markdown parse for
// EVERY message on EVERY event froze long conversations (31 Aug "Page
// Unresponsive"). With memo, only a message whose content changed re-parses.
export const MessageContent = memo(function MessageContent({ content }: MessageContentProps) {
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "")
            const isInline = !match
            if (isInline) {
              return (
                <code
                  className="bg-muted px-1 py-0.5 rounded text-xs font-mono"
                  {...props}
                >
                  {children}
                </code>
              )
            }
            return (
              <SyntaxHighlighter
                style={oneLight}
                language={match[1]}
                PreTag="div"
                className="rounded-md text-xs my-2"
              >
                {String(children).replace(/\n$/, "")}
              </SyntaxHighlighter>
            )
          },
          table({ children }) {
            return (
              <div className="overflow-x-auto my-2">
                <table className="min-w-full border-collapse text-xs">{children}</table>
              </div>
            )
          },
          th({ children }) {
            return (
              <th className="border border-border bg-muted px-2 py-1 text-left font-medium">{children}</th>
            )
          },
          td({ children }) {
            return <td className="border border-border px-2 py-1">{children}</td>
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
})
